import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Cloud, CloudOff, FolderOpen, Loader2, Play, RefreshCw, Unlink, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = window.location.origin + "/api";

export default function DriveBackupPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [folderId, setFolderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkState, setBulkState] = useState(null);
  const [jobs, setJobs] = useState([]);
  // DB backup separato (mongodump quotidiano su Drive/_database/YYYY-MM-DD.zip)
  const [dbJobs, setDbJobs] = useState([]);
  const [dbRetention, setDbRetention] = useState(30);
  const [dbRunning, setDbRunning] = useState(false);
  const pollRef = useRef(null);
  const dbPollRef = useRef(null);

  const loadDbJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/drive-backup/db/jobs?limit=10`, { withCredentials: true });
      const newJobs = data?.jobs || [];
      setDbJobs(newJobs);
      if (data?.retention_days) setDbRetention(data.retention_days);
      // Sync dbRunning sullo stato del job più recente, indipendentemente
      // dal valore corrente di dbRunning (la closure usata da setInterval
      // catturava il vecchio dbRunning=false e non chiudeva mai il poll).
      const last = newJobs[0];
      const stillRunning = last?.status === "running";
      setDbRunning(stillRunning);
      if (!stillRunning && dbPollRef.current) {
        clearInterval(dbPollRef.current);
        dbPollRef.current = null;
        if (last?.status === "ok") {
          toast.success(`Backup DB completato — ${last.filename}`);
        } else if (last?.status === "error") {
          toast.error("Backup DB fallito: " + (last.error || "errore sconosciuto"));
        }
      }
    } catch (err) {
      // Endpoint potrebbe non esistere se non ancora deployato
      console.warn("db jobs load failed:", err?.message);
    }
  }, []);

  const handleDbBackupRun = useCallback(async () => {
    if (dbRunning) return;
    if (!window.confirm("Avviare il backup completo del database su Drive? L'operazione può richiedere fino a qualche minuto.")) return;
    try {
      setDbRunning(true);
      await axios.post(`${API}/drive-backup/db/run`, {}, { withCredentials: true });
      toast.success("Backup DB avviato — verrà caricato su Drive a breve");
      // Poll ogni 5s. Il clear avviene dentro loadDbJobs quando lo status
      // dell'ultimo job esce da "running" (ok/error).
      if (dbPollRef.current) clearInterval(dbPollRef.current);
      dbPollRef.current = setInterval(loadDbJobs, 5000);
      // Safety net: chiudi il polling dopo 10 minuti anche se il job
      // rimane bloccato in "running" (evita spinner infinito).
      setTimeout(() => {
        if (dbPollRef.current) {
          clearInterval(dbPollRef.current);
          dbPollRef.current = null;
        }
        setDbRunning(false);
      }, 10 * 60 * 1000);
      // Initial reload after 2s
      setTimeout(loadDbJobs, 2000);
    } catch (err) {
      setDbRunning(false);
      toast.error(err.response?.data?.detail || "Errore avvio backup DB");
    }
  }, [dbRunning, loadDbJobs]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/drive-backup/status`, { withCredentials: true });
      setStatus(data);
      if (data?.folder_id) setFolderId(data.folder_id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/drive-backup/jobs?limit=20`, { withCredentials: true });
      setJobs(data?.jobs || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const pollBulk = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/drive-backup/bulk-status`, { withCredentials: true });
      setBulkState(data);
      if (!data?.running) {
        setBulkRunning(false);
        clearInterval(pollRef.current);
        pollRef.current = null;
        loadJobs();
      }
    } catch (err) {
      console.error(err);
    }
  }, [loadJobs]);

  useEffect(() => {
    loadStatus();
    loadJobs();
    loadDbJobs();
    pollBulk();
    // Check URL params for OAuth callback feedback
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive") === "connected") {
      toast.success("Google Drive connesso correttamente");
      // Clean the URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("drive") === "error") {
      toast.error("Errore connessione Drive: " + (params.get("reason") || "ignoto"));
      window.history.replaceState({}, "", window.location.pathname);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (dbPollRef.current) clearInterval(dbPollRef.current);
    };
  }, [loadStatus, loadJobs, loadDbJobs, pollBulk]);

  const handleConnect = () => {
    window.location.href = `${API}/drive-backup/connect`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnettere Google Drive? Il backup automatico sarà disattivato.")) return;
    try {
      await axios.post(`${API}/drive-backup/disconnect`, {}, { withCredentials: true });
      toast.success("Drive disconnesso");
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleSaveFolder = async () => {
    if (!folderId.trim()) {
      toast.error("Inserisci un Folder ID");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/drive-backup/config`,
        { folder_id: folderId.trim(), enabled: true },
        { withCredentials: true }
      );
      toast.success(`Cartella impostata: "${data.folder_name}"`);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleRunBulk = async () => {
    if (!status?.folder_id) {
      toast.error("Configura prima la cartella destinazione");
      return;
    }
    if (!window.confirm("Avviare il backup massivo di tutte le opportunità con foto? Può richiedere 15–30 minuti.")) return;
    setBulkRunning(true);
    try {
      await axios.post(
        `${API}/drive-backup/run-bulk`,
        { status: "open" },
        { withCredentials: true }
      );
      toast.success("Backup massivo avviato in background");
      // Start polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(pollBulk, 2500);
    } catch (err) {
      setBulkRunning(false);
      toast.error(err.response?.data?.detail || "Errore avvio backup");
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-100 flex items-center gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" /> Caricamento stato Drive…
      </div>
    );
  }

  const connected = !!status?.connected;
  const progressPct = bulkState?.total > 0
    ? Math.round((bulkState.processed / bulkState.total) * 100)
    : 0;

  return (
    <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-100 space-y-5" data-testid="drive-backup-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
            {connected ? <Cloud className="w-5 h-5" /> : <CloudOff className="w-5 h-5" />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Backup Foto su Google Drive</h2>
            <p className="text-xs text-slate-500">
              {connected
                ? <>Connesso come <strong>{status.account_email || "sconosciuto"}</strong></>
                : "Drive non connesso"}
            </p>
          </div>
        </div>
        {connected ? (
          <Button onClick={handleDisconnect} variant="outline" size="sm" className="rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="drive-disconnect-btn">
            <Unlink className="w-4 h-4 mr-1.5" /> Disconnetti
          </Button>
        ) : (
          <Button onClick={handleConnect} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="drive-connect-btn">
            <Cloud className="w-4 h-4 mr-2" /> Connetti Drive
          </Button>
        )}
      </div>

      {connected && (
        <>
          {/* Folder configuration */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Cartella destinazione (Folder ID)</Label>
            <p className="text-[11px] text-slate-500">
              Apri la cartella su Drive, copia l'ID dall'URL (es. <code className="bg-slate-200 px-1 rounded">/folders/<strong>1aBcDeFgHi…</strong></code>) e incollalo qui.
            </p>
            <div className="flex gap-2 items-center">
              <Input
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                placeholder="es. 1aBcDeFgHiJkLmNoPq…"
                className="h-10 bg-white rounded-xl flex-1"
                data-testid="drive-folder-input"
              />
              <Button onClick={handleSaveFolder} disabled={saving} className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl" data-testid="drive-save-folder-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-1.5" />}
                Salva
              </Button>
            </div>
            {status?.folder_name && (
              <p className="text-xs text-emerald-700 font-medium" data-testid="drive-folder-name">
                ✓ Cartella attiva: <strong>{status.folder_name}</strong>
              </p>
            )}
          </div>

          {/* DB backup (mongodump → Drive/_database) */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 space-y-3 border border-emerald-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <Database className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-slate-800">Backup database completo</h3>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    Esporta TUTTE le collezioni Mongo (utenti, opportunità, history, contatti…) in un singolo zip e lo carica in
                    <code className="mx-1 bg-white/80 px-1 rounded">{(status.folder_name || "Backup BDD") + "/_database/"}</code>
                    Esecuzione automatica ogni notte alle <strong>03:00 Roma</strong>. Conservazione {dbRetention} giorni.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleDbBackupRun}
                disabled={dbRunning || !status?.folder_id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex-shrink-0"
                data-testid="drive-db-backup-now-btn"
              >
                {dbRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
                {dbRunning ? "Backup in corso…" : "Backup ora"}
              </Button>
            </div>
            {dbJobs.length > 0 && (
              <div className="space-y-1.5 pt-1 max-h-40 overflow-y-auto" data-testid="drive-db-backup-jobs">
                {dbJobs.slice(0, 5).map((j) => (
                  <div key={j.job_id} className="text-[11px] text-slate-600 flex items-center justify-between gap-2 bg-white/70 rounded-lg px-3 py-1.5">
                    <span className="font-mono text-slate-400">{(j.started_at || "").slice(0, 19).replace("T", " ")}</span>
                    <span className="font-medium truncate">{j.filename || "—"}</span>
                    <span className="text-slate-500">{j.total_docs ? `${j.total_docs.toLocaleString()} doc` : "—"}</span>
                    <span className="text-slate-500">{j.size_bytes ? `${(j.size_bytes / (1024*1024)).toFixed(1)} MB` : "—"}</span>
                    {j.status === "ok" && <span className="text-emerald-600 font-semibold">✓</span>}
                    {j.status === "running" && <Loader2 className="w-3 h-3 animate-spin text-amber-500" />}
                    {j.status === "error" && <span className="text-rose-600 font-semibold" title={j.error}>✗</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bulk backup */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Backup massivo storico</h3>
                <p className="text-[11px] text-slate-500">
                  Esegue il backup di TUTTE le opportunità con foto. Re-eseguibile: i file già presenti vengono saltati.
                </p>
              </div>
              <Button
                onClick={handleRunBulk}
                disabled={bulkRunning || !status?.folder_id}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl flex-shrink-0"
                data-testid="drive-run-bulk-btn"
              >
                {bulkRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
                {bulkRunning ? "In corso…" : "Avvia backup massivo"}
              </Button>
              {bulkRunning && (
                <Button
                  onClick={async () => {
                    if (!window.confirm("Forzare il reset del job? Lo stato sarà azzerato (le foto già copiate restano su Drive).")) return;
                    try {
                      await axios.post(`${API}/drive-backup/bulk-reset`, {}, { withCredentials: true });
                      toast.success("Stato bulk azzerato");
                      setBulkRunning(false);
                      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                      pollBulk();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Errore");
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50 flex-shrink-0"
                  data-testid="drive-bulk-reset-btn"
                >
                  Reset stato
                </Button>
              )}
            </div>

            {bulkState && (bulkState.running || bulkState.total > 0) && (
              <div className="space-y-1.5 pt-2" data-testid="drive-bulk-progress">
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>{bulkState.processed} / {bulkState.total} opp · {bulkState.uploaded} caricate · {bulkState.skipped} saltate · {bulkState.failed} fallite</span>
                  <span className="font-semibold">{progressPct}%</span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bulkState.running ? "bg-amber-500" : "bg-emerald-500"} transition-all`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {bulkState.current && bulkState.running && (
                  <p className="text-[11px] text-slate-500 truncate">▶ Sto processando: <strong>{bulkState.current}</strong></p>
                )}
              </div>
            )}
          </div>

          {/* Job log */}
          {jobs.length > 0 && (
            <div className="bg-slate-50 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">Cronologia ultimi backup</h3>
                <button onClick={loadJobs} className="text-slate-400 hover:text-slate-600" title="Aggiorna">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {jobs.slice(0, 10).map((j) => (
                  <div key={j.id || j.created_at || j.action} className="text-[11px] text-slate-600 flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-1.5">
                    <span className="font-mono text-slate-400">{j.created_at?.slice(0, 19).replace("T", " ")}</span>
                    <span className="font-medium">{j.action}</span>
                    <span className="text-emerald-600">↑ {j.details?.uploaded ?? 0}</span>
                    <span className="text-slate-400">— {j.details?.skipped ?? 0}</span>
                    {(j.details?.failed ?? 0) > 0 && <span className="text-rose-600">✗ {j.details.failed}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-400 bg-blue-50 border border-blue-200 rounded-xl p-3">
            <strong className="text-blue-700">Struttura cartelle:</strong> <code>{status.folder_name || "Backup BDD"}</code>/<code>Cognome Nome</code>/<code>DD-MM-YYYY - Reparto</code>/<code>foto.jpg</code><br />
            Il backup automatico è attivo: ogni Piano di Cura creato/modificato verrà copiato su Drive in background (best-effort, non blocca il salvataggio).<br />
            <span className="block mt-1 text-emerald-700">
              <strong>Sync notturno automatico</strong> ogni giorno alle <strong>02:00 Roma</strong>: recupera anche eventuali foto caricate fuori dall'app o non sincronizzate (idempotente, salta quelle già su Drive).
            </span>
          </div>
        </>
      )}

      {!connected && (
        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <strong className="text-amber-700">Setup richiesto:</strong> in <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a> aggiungi questi redirect URI alle credenziali OAuth e abilita lo scope <code>drive</code>:
          <ul className="list-disc list-inside mt-1.5 ml-2 space-y-0.5 font-mono text-[10px]">
            <li>https://opportunity-builder.preview.emergentagent.com/api/drive-backup/callback</li>
            <li>https://backoffice.bddstudiodentistico.it/api/drive-backup/callback</li>
          </ul>
        </div>
      )}
    </div>
  );
}
